import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Category } from '../types';
import { apiService } from '../services/api';

interface SyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: {
    categoriaId?: string | null;
    deleteExisting: boolean;
    queryParams: Record<string, string>;
    createCategoryIfNotExists: boolean;
    newCategoryName?: string;
  }) => Promise<void>;
  onCancel?: () => Promise<void>;
  loading?: boolean;
}

export function SyncModal({ isOpen, onClose, onConfirm, onCancel, loading = false }: SyncModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [queryParams, setQueryParams] = useState<Record<string, string>>({});
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [deleteExisting, setDeleteExisting] = useState(true);
  const [createNewCategory, setCreateNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadIntegrationUrl();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await apiService.getAllCategories();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadIntegrationUrl = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const url = data.contactIntegrationUrl || '';
        if (url) {
          try {
            const urlObj = new URL(url);
            const params: Record<string, string> = {};
            urlObj.searchParams.forEach((value, key) => {
              params[key] = value;
            });
            setQueryParams(params);
          } catch {
            setQueryParams({});
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar URL de integração:', error);
    }
  };

  const handleQueryParamChange = (key: string, value: string) => {
    setQueryParams(prev => ({ ...prev, [key]: value }));
  };

  const handleConfirm = async () => {
    if (createNewCategory && !newCategoryName.trim()) {
      toast.error('Informe o nome da nova categoria');
      return;
    }
    try {
      await onConfirm({
        categoriaId: selectedCategoryId ? selectedCategoryId : null,
        deleteExisting,
        queryParams,
        createCategoryIfNotExists: createNewCategory,
        newCategoryName: createNewCategory ? newCategoryName.trim() : undefined
      });
      onClose();
      setSelectedCategoryId('');
      setDeleteExisting(true);
      setCreateNewCategory(false);
      setNewCategoryName('');
    } catch (error) {
      // Erro já tratado no componente pai
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Configurar Sincronização</h3>
                <p className="text-sm text-gray-500">Use o endpoint configurado em Configurações para importar contatos</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoria de Destino *</label>
              <div className="space-y-3">
                <select
                  value={selectedCategoryId}
                  onChange={(e) => { setSelectedCategoryId(e.target.value); setCreateNewCategory(false); }}
                  disabled={loading || loadingCategories || createNewCategory}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  <option value="">Selecione uma categoria existente</option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.nome}</option>
                  ))}
                </select>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="createNewCategory"
                    checked={createNewCategory}
                    onChange={(e) => { setCreateNewCategory(e.target.checked); if (e.target.checked) setSelectedCategoryId(''); }}
                    disabled={loading}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <label htmlFor="createNewCategory" className="ml-2 text-sm text-gray-700">Criar nova categoria</label>
                </div>
                {createNewCategory && (
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nome da nova categoria"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Opções de Sincronização</label>
              <div className="space-y-2">
                <label className="flex items-start p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="radio" name="syncMode" checked={deleteExisting} onChange={() => setDeleteExisting(true)} disabled={loading} className="mt-1 text-orange-600 focus:ring-orange-500" />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-700">Excluir contatos existentes da categoria</div>
                    <div className="text-xs text-gray-500 mt-1">Remove todos os contatos da categoria antes de importar</div>
                  </div>
                </label>
                <label className="flex items-start p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="radio" name="syncMode" checked={!deleteExisting} onChange={() => setDeleteExisting(false)} disabled={loading} className="mt-1 text-orange-600 focus:ring-orange-500" />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-700">Adicionar novos (manter existentes)</div>
                    <div className="text-xs text-gray-500 mt-1">Mantém os contatos existentes e adiciona os novos</div>
                  </div>
                </label>
              </div>
            </div>

            {Object.keys(queryParams).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parâmetros da URL de Integração</label>
                <div className="space-y-2">
                  {Object.entries(queryParams).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-600 w-32 flex-shrink-0">{key}:</label>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleQueryParamChange(key, e.target.value)}
                        disabled={loading}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Atenção:</strong> A sincronização importa contatos do sistema externo (URL configurada em Configurações).
                {deleteExisting && <span className="block mt-1">Todos os contatos da categoria selecionada serão excluídos antes da importação.</span>}
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            {loading && onCancel ? (
              <button onClick={onCancel} disabled={!loading} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
                Parar Sincronização
              </button>
            ) : (
              <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">
                Cancelar
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={loading || (!selectedCategoryId && !createNewCategory) || (createNewCategory && !newCategoryName.trim())}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                  Sincronizando...
                </span>
              ) : (
                'Confirmar Sincronização'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
